/**
 * Pharmacy platform modules:
 * - pharmacyPortal  — pharmacy_admin / pharmacy_staff portal actions
 * - pharmacyAdmin   — super-admin pharmacy management
 * - pharmacyMedications — medication inventory
 */
import { fetchApi } from '../client';
import type {
  Pharmacy,
  CreatePharmacyUserRequest,
  CreatePharmacyUserResponse,
  PharmacyInvitation,
  PharmacyRegistrationRequest,
  PharmacyInviteRequest,
  PharmacySearchRequest,
  PharmacyStatistics,
  PharmacyUser,
  PharmacyPrescriptionDelivery,
  PharmacyPrescriptionStats,
  CreateInHousePharmacyRequest,
  InHousePharmacyResponse,
  InvitePharmacyStaffRequest,
  PharmacyStaffInvitationResponse,
  FulfillmentStatus,
} from '../../../types';
import type { PagedResponse } from '../../../types/pagination';

// ── Pharmacy Portal (pharmacy_admin / pharmacy_staff) ──────────────────────

/** One line of the accept / dispense-now body: backend AcceptPrescriptionRequest.MedicationFulfillmentItem. */
export interface MedicationFulfillmentItem {
  prescriptionMedicationId?: string;
  medicationName?: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  instructions?: string;
  status?: FulfillmentStatus;
  requestedQuantity?: number;
  fulfilledQuantity?: number;
  unit?: string;
  unitPrice?: number;
  totalPrice?: number;
  /** Required for UNAVAILABLE and PARTIALLY_AVAILABLE lines. */
  unavailabilityReason?: string;
  pharmacyNotes?: string;
  /** Required for SUBSTITUTED lines: the stock item handed over instead. */
  substituteMedication?: string;
  substituteApprovedByDoctor?: boolean;
  pharmacyMedicationId?: string;
  batchNumber?: string;
  /** ISO date. */
  expiryDate?: string;
  manufacturer?: string;
  displayOrder?: number;
}

/** Body of POST /pharmacy/prescriptions/{id}/accept and /dispense-now (backend AcceptPrescriptionRequest). */
export interface AcceptPrescriptionRequest {
  pharmacyNotes?: string;
  medications?: MedicationFulfillmentItem[];
}

async function unwrap<T>(res: Awaited<ReturnType<typeof fetchApi<T>>>): Promise<T> {
  if (res.error) throw new Error(res.error);
  return res.data as T;
}

export const pharmacyPortalModule = {
  getMyPharmacies: async () =>
    unwrap(await fetchApi<PharmacyUser[]>('/api/pharmacy/my-pharmacies')),

  getPendingPrescriptions: async (pharmacyId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery[]>(
      `/api/pharmacy/${pharmacyId}/prescriptions/pending`
    )),

  getPrescriptionDetails: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(`/api/pharmacy/prescriptions/${deliveryId}`)),

  acceptPrescription: async (deliveryId: string, fulfillmentData?: AcceptPrescriptionRequest) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/accept`,
      { method: 'POST', body: JSON.stringify(fulfillmentData || {}) }
    )),

  rejectPrescription: async (deliveryId: string, rejectionReason: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/reject`,
      { method: 'POST', body: JSON.stringify({ rejectionReason }) }
    )),

  markAsPrepared: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/mark-prepared`,
      { method: 'POST' }
    )),

  markAsCollected: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/mark-collected`,
      { method: 'POST' }
    )),

  getPrescriptionHistory: async (pharmacyId: string, status?: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery[]>(
      status
        ? `/api/pharmacy/${pharmacyId}/prescriptions/history?status=${status}`
        : `/api/pharmacy/${pharmacyId}/prescriptions/history`
    )),

  /** One page of the prescription history (newest first), optionally in one status (medopd-docs 112). */
  getPrescriptionHistoryPage: async (pharmacyId: string, params: { status?: string; page: number; size: number }) => {
    const qs = new URLSearchParams({ page: String(params.page), size: String(params.size) });
    if (params.status) qs.set('status', params.status);
    return unwrap(await fetchApi<PagedResponse<PharmacyPrescriptionDelivery>>(
      `/api/pharmacy/${pharmacyId}/prescriptions/history?${qs}`
    ));
  },

  getStatistics: async (pharmacyId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionStats>(`/api/pharmacy/${pharmacyId}/statistics`)),

  listAssigned: async (_clinicId: string) =>
    unwrap(await fetchApi<Pharmacy[]>('/api/doctor/pharmacies?activeOnly=true')),

  // ── medopd-docs 136 §6 ────────────────────────────────────────────────────

  /** Counter dispensing in one step: accept with the per-line decisions, then collect (one transaction). */
  dispenseNow: async (deliveryId: string, fulfillmentData?: AcceptPrescriptionRequest) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/dispense-now`,
      { method: 'POST', body: JSON.stringify(fulfillmentData || {}) }
    )),

  /** The pharmacy's own stock check for one of its deliveries (runs when the accept dialog opens). */
  getDeliveryAvailability: async (deliveryId: string) =>
    unwrap(await fetchApi<DeliveryAvailability>(`/api/pharmacy/prescriptions/${deliveryId}/availability`)),

  /** Whether the clinic has switched online medicine ordering on for this pharmacy. */
  getOnlineOrdering: async (pharmacyId: string) =>
    unwrap(await fetchApi<{ enabled: boolean }>(`/api/pharmacy/${pharmacyId}/online-ordering`)),

  /** Verified inpatient medication orders awaiting dispense at the pharmacy's clinic. */
  getWardOrders: async (pharmacyId: string) =>
    unwrap(await fetchApi<WardOrder[]>(`/api/pharmacy/${pharmacyId}/ward-orders`)),

  dispenseWardOrder: async (pharmacyId: string, orderId: string, quantity: number) =>
    unwrap(await fetchApi<unknown>(`/api/pharmacy/${pharmacyId}/ward-orders/${orderId}/dispense`,
      { method: 'POST', body: JSON.stringify({ quantity }) })),

  /** Pre-approval sign-in message: true when the address belongs to a pharmacy still awaiting approval. */
  getRegistrationStatus: async (email: string) =>
    unwrap(await fetchApi<{ awaitingApproval: boolean }>(
      `/api/public/pharmacy/registration-status?email=${encodeURIComponent(email)}`, { skipAuth: true }
    )),
};

/** One stock row as the pharmacy-side availability check reports it. */
export interface StockItemView {
  id: string;
  medicationName: string;
  genericName?: string | null;
  strength?: string | null;
  dosageForm?: string | null;
  unit?: string | null;
  currentQuantity: number | null;
  unitPrice?: number | null;
  batchNumber?: string | null;
  expiryDate?: string | null;
  expired: boolean;
  drugSchedule?: string | null;
}

export interface DeliveryAvailabilityLine {
  prescriptionMedicationId: string;
  medicationName: string;
  /** MATCHED, SHORT, EXPIRED or UNAVAILABLE. */
  availability: 'MATCHED' | 'SHORT' | 'EXPIRED' | 'UNAVAILABLE';
  matchRule?: string | null;
  stock?: StockItemView | null;
  suggestedQuantity: number;
  quantityFormula: string;
  substitutes: StockItemView[];
}

export interface DeliveryAvailability {
  deliveryId: string;
  pharmacyId: string;
  inventoryTracked: boolean;
  lines: DeliveryAvailabilityLine[];
}

export interface WardOrder {
  orderId: string;
  admissionId: string;
  admissionNumber?: string | null;
  patientName?: string | null;
  wardName?: string | null;
  bedNumber?: string | null;
  medicationName: string;
  dosage?: string | null;
  route?: string | null;
  frequency?: string | null;
  isPrn?: boolean | null;
  status?: string | null;
  verifiedAt?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  suggestedQuantity: number;
  quantityFormula?: string | null;
  stockId?: string | null;
  stockName?: string | null;
  stockQuantity?: number | null;
  stockUnit?: string | null;
  inStock: boolean;
}

// ── Pharmacy Admin (super-admin / clinic-admin) ────────────────────────────

export const pharmacyAdminModule = {
  // Super-admin user management
  createPharmacyUser: (data: CreatePharmacyUserRequest) =>
    fetchApi<CreatePharmacyUserResponse>('/api/admin/pharmacy-users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPendingInvitations: () =>
    fetchApi<PharmacyInvitation[]>('/api/admin/pharmacy-users/invitations/pending'),

  resendInvitation: (invitationId: string) =>
    fetchApi<{ message: string }>(
      `/api/admin/pharmacy-users/invitations/${invitationId}/resend`,
      { method: 'POST' }
    ),

  cancelInvitation: (invitationId: string) =>
    fetchApi<void>(`/api/admin/pharmacy-users/invitations/${invitationId}`, {
      method: 'DELETE',
    }),

  // Pharmacy lifecycle
  registerPharmacy: (data: PharmacyRegistrationRequest) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy; status: string }>(
      '/api/public/pharmacy/register',
      { method: 'POST', body: JSON.stringify(data), skipAuth: true }
    ),

  acceptInvitation: (token: string, data: PharmacyRegistrationRequest) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy; status: string }>(
      `/api/public/pharmacy/accept-invitation?token=${token}`,
      { method: 'POST', body: JSON.stringify(data), skipAuth: true }
    ),

  invitePharmacy: (data: PharmacyInviteRequest) =>
    fetchApi<{ success: boolean; message: string; invitationId: string; email: string; expiresAt: string; pharmacyName: string }>(
      '/api/admin/pharmacy-invitations',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  getPendingApprovals: () =>
    fetchApi<Pharmacy[]>('/api/admin/pharmacies/pending'),

  /** One page of pending applications, oldest first (medopd-docs 117). */
  getPendingApprovalsPage: (page: number, size: number) =>
    fetchApi<PagedResponse<Pharmacy>>(`/api/admin/pharmacies/pending?page=${page}&size=${size}`),

  approve: (pharmacyId: string, notes?: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/approve`,
      { method: 'POST', body: JSON.stringify({ notes }) }
    ),

  reject: (pharmacyId: string, reason: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  suspend: (pharmacyId: string, reason: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/suspend`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  reactivate: (pharmacyId: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/reactivate`,
      { method: 'POST' }
    ),

  getStatistics: () =>
    fetchApi<PharmacyStatistics>('/api/admin/pharmacies/statistics'),

  // Super-admin registry list with per-pharmacy activation status (all statuses unless filtered).
  // Backend caps pageSize at 200; the approval screen filters/counts client-side, so ask for the max.
  getAllWithActivation: (status?: string) =>
    fetchApi<unknown[]>(
      `/api/admin/pharmacies/all?pageSize=200${status ? `&status=${encodeURIComponent(status)}` : ''}`
    ),

  // Server-paged registry (medopd-docs 112): `size` switches the endpoint to the PagedResponse shape with
  // totals; status + search are applied in SQL so they cover every pharmacy, not just one page.
  getAllWithActivationPaged: (params: { page: number; size: number; status?: string; search?: string }) => {
    const p = new URLSearchParams({ page: String(params.page), size: String(params.size) });
    if (params.status) p.set('status', params.status);
    if (params.search) p.set('search', params.search);
    return fetchApi<PagedResponse<Record<string, unknown>>>(`/api/admin/pharmacies/all?${p.toString()}`);
  },

  getAll: (activeOnly?: boolean) =>
    fetchApi<Pharmacy[]>(activeOnly ? '/api/pharmacies?activeOnly=true' : '/api/pharmacies'),

  resendActivation: (pharmacyId: string) =>
    fetchApi<{ success: boolean; message: string }>(
      `/api/admin/pharmacies/${pharmacyId}/resend-activation`,
      { method: 'POST' }
    ),

  sendInitialActivation: (pharmacyId: string) =>
    fetchApi<{ success: boolean; message: string }>(
      `/api/admin/pharmacies/${pharmacyId}/send-initial-activation`,
      { method: 'POST' }
    ),

  deactivate: (pharmacyId: string, reason: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/deactivate`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  search: (searchRequest: PharmacySearchRequest) =>
    fetchApi<Pharmacy[]>('/api/pharmacies/search', {
      method: 'POST',
      body: JSON.stringify(searchRequest),
    }),

  // In-house pharmacy management (clinic admin)
  createInHousePharmacy: (data: CreateInHousePharmacyRequest) =>
    fetchApi<InHousePharmacyResponse>('/api/clinic/in-house-pharmacies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getClinicInHousePharmacies: () =>
    fetchApi<InHousePharmacyResponse[]>('/api/clinic/in-house-pharmacies'),

  getClinicActivePharmacies: () =>
    fetchApi<InHousePharmacyResponse[]>('/api/clinic/in-house-pharmacies?active=true'),

  getInHousePharmacy: (pharmacyId: string) =>
    fetchApi<InHousePharmacyResponse>(`/api/clinic/in-house-pharmacies/${pharmacyId}`),

  updateInHousePharmacy: (pharmacyId: string, data: Partial<CreateInHousePharmacyRequest>) =>
    fetchApi<InHousePharmacyResponse>(`/api/clinic/in-house-pharmacies/${pharmacyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deactivateInHousePharmacy: (pharmacyId: string) =>
    fetchApi<{ message: string }>(`/api/clinic/in-house-pharmacies/${pharmacyId}`, {
      method: 'DELETE',
    }),

  invitePharmacyStaff: (pharmacyId: string, data: InvitePharmacyStaffRequest) =>
    fetchApi<PharmacyStaffInvitationResponse>(
      `/api/clinic/in-house-pharmacies/${pharmacyId}/staff/invite`,
      { method: 'POST', body: JSON.stringify(data) }
    ),
};

// ── Pharmacy Medication Inventory ──────────────────────────────────────────

function throwOnError<T>(res: { data?: T; error?: string }): T {
  if (res.error) throw new Error(res.error);
  return res.data as T;
}

export const pharmacyMedicationsModule = {
  create: async (data: any) => throwOnError(await fetchApi<any>('/api/pharmacy-medications', { method: 'POST', body: JSON.stringify(data) })),
  list: async () => throwOnError(await fetchApi<any[] | { data?: any[] }>('/api/pharmacy-medications')) ?? [],
  /**
   * One page of the stock for the inventory screen; search and the stock filter run on the server so they
   * cover the whole stock (medopd-docs 112). `list()` still returns everything for the pickers.
   */
  listPaged: async (params: { page: number; size: number; search?: string; filter?: string; sort?: string }) => {
    const p = new URLSearchParams({ page: String(params.page), size: String(params.size) });
    if (params.search) p.set('search', params.search);
    if (params.filter && params.filter !== 'all') p.set('filter', params.filter);
    if (params.sort) p.set('sort', params.sort);
    return throwOnError(await fetchApi<PagedResponse<any>>(`/api/pharmacy-medications?${p.toString()}`));
  },
  /** Counts behind the inventory screen's filter buttons (medopd-docs 112). */
  summary: async () => throwOnError(await fetchApi<{ total: number; lowStock: number; expired: number; expiringSoon: number; unclassified: number;
    outOfStock?: number; expiring90?: number; stockValue?: number }>('/api/pharmacy-medications/summary')),
  search: async (query: string) => throwOnError(await fetchApi<any[] | { data?: any[] }>(`/api/pharmacy-medications/search?query=${encodeURIComponent(query)}`)) ?? [],
  getLowStock: async () => throwOnError(await fetchApi<any[] | { data?: any[] }>('/api/pharmacy-medications/low-stock')) ?? [],
  getExpired: async () => throwOnError(await fetchApi<any[] | { data?: any[] }>('/api/pharmacy-medications/expired')) ?? [],
  getExpiringSoon: async () => throwOnError(await fetchApi<any[] | { data?: any[] }>('/api/pharmacy-medications/expiring-soon')) ?? [],
  getById: async (id: string) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}`)),
  update: async (id: string, data: any) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}`, { method: 'PUT', body: JSON.stringify(data) })),
  updateStock: async (id: string, newQuantity: number) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}/stock`, { method: 'PUT', body: JSON.stringify({ newQuantity }) })),
  updatePrice: async (id: string, newPrice: number) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}/price`, { method: 'PUT', body: JSON.stringify({ newPrice }) })),
  delete: async (id: string) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}`, { method: 'DELETE' })),
  /** Bulk drug-schedule classification (blank = not classified). Pharmacy staff and clinic admins only. */
  classifySchedules: async (items: Array<{ id: string; drugSchedule: string }>) =>
    throwOnError(await fetchApi<any>('/api/pharmacy-medications/schedules', { method: 'PUT', body: JSON.stringify({ items }) })),
  /** Receive stock: ADD units with the delivery's batch/expiry/MRP (medopd-docs 136 §6). Pharmacy staff may call it. */
  receiveStock: async (id: string, data: ReceiveStockInput) =>
    throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}/receive`, { method: 'POST', body: JSON.stringify(data) })),
  /** CSV import: dryRun=true previews (CREATE/ADD/ERROR per row) without saving; dryRun=false applies. */
  importStock: async (rows: StockImportRow[], dryRun: boolean) =>
    throwOnError(await fetchApi<StockImportResult>('/api/pharmacy-medications/import', { method: 'POST', body: JSON.stringify({ rows, dryRun }) })),
  /** Starts the import in the background and returns at once (medopd-docs 142); the tracker follows the job. */
  startImportJob: async (fileName: string, rows: StockImportRow[]) =>
    throwOnError(await fetchApi<{ id: string; status: string; totalRows: number }>('/api/pharmacy-medications/import-jobs', { method: 'POST', body: JSON.stringify({ fileName, rows }) })),
  importJob: async (id: string) =>
    throwOnError(await fetchApi<StockImportJob>(`/api/pharmacy-medications/import-jobs/${encodeURIComponent(id)}`)),
  importJobs: async (limit = 5) =>
    throwOnError(await fetchApi<StockImportJob[]>(`/api/pharmacy-medications/import-jobs?limit=${limit}`)),
};

/** A background stock import (medopd-docs 142). */
export interface StockImportJob {
  id: string;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  fileName?: string | null;
  totalRows: number;
  processedRows: number;
  createdCount: number;
  addedCount: number;
  errorCount: number;
  errorMessage?: string | null;
  durationMs?: number | null;
  createdAt?: string;
  finishedAt?: string | null;
  errorRows?: Array<{ line?: number; name?: string; strength?: string; batch?: string; error?: string }>;
}

export interface ReceiveStockInput {
  quantity: number;
  batchNumber?: string;
  expiryDate?: string;
  unitPrice?: number;
  manufacturer?: string;
  notes?: string;
}

export interface StockImportRow {
  line: number;
  name: string;
  genericName?: string;
  strength?: string;
  form?: string;
  batch?: string;
  expiry?: string;
  quantity: number | null;
  mrp?: string;
  unit?: string;
  manufacturer?: string;
  drugSchedule?: string;
  /** Optional (medopd-docs 142): HSN code, GST %, purchase rate, barcode. */
  hsn?: string;
  gst?: string;
  ptr?: string;
  barcode?: string;
}

export interface StockImportResult {
  dryRun: boolean;
  total: number;
  creates: number;
  additions: number;
  errors: number;
  rows: Array<{
    line: number | null;
    name: string | null;
    strength: string | null;
    form: string | null;
    batch: string | null;
    quantity: number | null;
    action: 'CREATE' | 'ADD' | 'ERROR';
    existingId?: string | null;
    existingQuantity?: number | null;
    error?: string | null;
  }>;
}
